/// <amd-module name="Search/Code/NavigationHandler" />

import * as Shared from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import { ActionCreator } from "Search/Code/Flux/ActionCreator";
import { IAggregatedState } from "Search/Code/Flux/StoresHub";
import { deserialize, areFiltersEqual, serializeFilters, serializeSortOptions, areSortOptionsEqual, stripOffRelevance } from "Search/Shared/Utils";
import { getItemResultKey } from "Search/Code/Utils";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssHistoryService } from "VSS/Platform/History"
import { SortActionIds } from "./Constants";

export function applyNavigatedUrl(
    actionCreator: ActionCreator,
    rawState: { [key: string]: string },
    aggregateState: IAggregatedState,
    serverSortEnabled: boolean): void {
    const { searchStoreState, previewStoreState, appliedEntitySortOption } = aggregateState;
    const { query } = searchStoreState;
    const filtersFromUrl = deserialize<{ [id: string]: string[] }>(rawState.filters) || {};
    const sortOptionsFromUrl = deserialize<Shared.EntitySortOption[]>(rawState.sortOptions) || [];

    let issueNewSearch = rawState.text && query.searchText !== rawState.text;
    issueNewSearch = issueNewSearch || !areFiltersEqual(filtersFromUrl, query.searchFilters);

    const sortOptionsChanged = !areSortOptionsEqual(
        stripOffRelevance(sortOptionsFromUrl, SortActionIds.Relevance),
        stripOffRelevance([appliedEntitySortOption], SortActionIds.Relevance));

    issueNewSearch = issueNewSearch || (serverSortEnabled && sortOptionsChanged);

    if (issueNewSearch) {
        actionCreator.performSearch(rawState.text, sortOptionsFromUrl, filtersFromUrl);
    } else if (rawState.action !== previewStoreState.activeTabKey) {
        actionCreator.changeActiveTab(rawState.action, true);
    } else if (!rawState.text) {
        actionCreator.showLandingPage(rawState.filters);
    }
}

export function updateUrl(
    pageContext: IVssPageContext,
    aggregateState: IAggregatedState,
    serverSortEnabled: boolean,
    includeFacets: boolean): void {
    const historyService = pageContext.getService<IVssHistoryService>("IVssHistoryService");
    let params: { [key: string]: string } = {};
    const { searchStoreState, previewStoreState, itemContentStoreState, appliedEntitySortOption } = aggregateState;
    const { query } = searchStoreState;

    let addHistoryPoint = false, replaceHistoryPoint = false;

    const currentParams = historyService.getState();

    if (query.searchText !== currentParams.text) {
        addHistoryPoint = true;
        params.text = query.searchText;
    }

    const filtersInUrl = deserialize<{ [id: string]: string[] }>(currentParams.filters) || {};
    if (!areFiltersEqual(query.searchFilters, filtersInUrl)) {
        addHistoryPoint = true;
        params.filters = serializeFilters(query.searchFilters)
    }

    if (previewStoreState.activeTabKey &&
        previewStoreState.activeTabKey !== currentParams.action) {
        addHistoryPoint = true;
        params.action = previewStoreState.activeTabKey;
    }

    const selectedItemKey = getItemResultKey(itemContentStoreState.selectedItem);
    if (selectedItemKey && selectedItemKey !== currentParams.result) {
        replaceHistoryPoint = true;
        params.result = selectedItemKey;
    }

    if (serverSortEnabled) {
        const sortOptionsInUrl = deserialize<Shared.EntitySortOption[]>(currentParams.sortOptions) || [];
        const sortOptionsChanged = !areSortOptionsEqual(
            stripOffRelevance(sortOptionsInUrl, SortActionIds.Relevance),
            stripOffRelevance([appliedEntitySortOption], SortActionIds.Relevance));

        if (sortOptionsChanged) {
            addHistoryPoint = true;
            params.sortOptions = serializeSortOptions([appliedEntitySortOption]);
        }
    }

    if (!includeFacets) {
        params.includeFacets = "false";
    }

    const newParams = { ...currentParams, ...params };

    // Replace is given precedence over adding history.
    if (replaceHistoryPoint) {
        historyService.replaceState(newParams);
    } else if (addHistoryPoint) {
        historyService.pushState(newParams);
    }
}